import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './lib/auth';
import { DataProvider } from './lib/data';
import { ConfirmProvider } from './lib/confirm';
import LoginPage from './pages/LoginPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import PlantafelPage from './pages/PlantafelPage';
import EmployeesPage from './pages/EmployeesPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import AbsencesPage from './pages/AbsencesPage';
import SettingsPage from './pages/SettingsPage';
import ProfilePage from './pages/ProfilePage';
import ActivityLogPage from './pages/ActivityLogPage';
import TimeImportPage from './pages/TimeImportPage';
import AttendancePage from './pages/AttendancePage';
import DayViewPage from './pages/DayViewPage';
import DevicesPage from './pages/DevicesPage';
import DevicePlantafelPage from './pages/DevicePlantafelPage';
import Layout from './components/Layout';
import './App.css';

/**
 * Erste Seite, die dieser Nutzer tatsächlich sehen darf.
 *
 * Früher führte jede abgelehnte Prüfung auf /dashboard – auch dann, wenn dem
 * Nutzer genau dieses Recht fehlte. Wer kein Dashboard-Recht hatte, landete
 * also auf einer Seite, die ihm ebenfalls verwehrt ist, und sah nur
 * Fehlermeldungen. Deshalb wird das Ziel jetzt aus den Rechten abgeleitet.
 */
function ersteErlaubteSeite(can) {
  const reihenfolge = [
    ['dashboard', '/dashboard'],
    ['plantafel', '/plantafel'],
    ['day_view', '/tagesansicht'],
    ['device_plantafel', '/geraete-plantafel'],
    ['projects', '/projekte'],
    ['employees', '/mitarbeiter'],
    ['absences', '/abwesenheiten'],
    ['attendance_timesheets', '/anwesenheit'],
    ['devices', '/geraete'],
    ['time_import', '/zeitimport'],
  ];
  const treffer = reihenfolge.find(([res]) => can(res, 'view'));
  // Bleibt nichts übrig, ist das eigene Profil immer erreichbar.
  return treffer ? treffer[1] : '/profil';
}

const Laedt = () => (
  <div className="min-h-screen flex items-center justify-center text-slate-500">Lädt…</div>
);

/**
 * Sind die Rechte schon da?
 *
 * Die Rechtematrix kommt über einen eigenen Aufruf und ist beim ersten Rendern
 * noch nicht geladen. Solange sie fehlt, antwortet can() mit "nein" – für
 * jede Seite. Wer dann weiterleitet, landet beim Profil und bleibt dort, denn
 * eine Weiterleitung wird nicht nochmal überdacht.
 *
 * Genau das passierte Betrachtern: Sie landeten beim Anmelden in den
 * Profil-Einstellungen statt in der Tagesansicht. Administratoren merkten
 * nichts davon, weil can() bei ihnen ohne Matrix auskommt.
 *
 * Also: erst warten, dann entscheiden.
 */
function rechteBereit(user, permMatrix) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return !!permMatrix;
}

function Protected({ children, requireAdmin = false, requirePerm = null }) {
  const { user, loading, can, permMatrix } = useAuth();
  if (loading) return <Laedt />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.force_password_change) return <Navigate to="/profil" replace />;
  if (!rechteBereit(user, permMatrix)) return <Laedt />;
  if (requireAdmin && user.role !== 'admin') return <Navigate to={ersteErlaubteSeite(can)} replace />;
  if (requirePerm && !can(requirePerm[0], requirePerm[1] || 'view')) return <Navigate to={ersteErlaubteSeite(can)} replace />;
  return children;
}

function StartSeite() {
  const { user, can, permMatrix } = useAuth();
  if (!rechteBereit(user, permMatrix)) return <Laedt />;
  return <Navigate to={ersteErlaubteSeite(can)} replace />;
}

// Rollen-Gate: entscheidet die gesamte App-Struktur je nach Rolle.
function ProtectedShell() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Lädt…</div>;
  if (!user) return <Navigate to="/login" replace />;

  if (user.force_password_change) {
    return (
      <Routes>
        <Route path="/profil" element={<ProfilePage />} />
        <Route path="*" element={<Navigate to="/profil" replace />} />
      </Routes>
    );
  }

  // Alle Rollen arbeiten im selben Layout. Was jemand sieht, entscheidet
  // ausschließlich die Berechtigungsmatrix – nicht mehr die Rolle.
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<StartSeite />} />
        <Route path="dashboard" element={<Protected requirePerm={['dashboard','view']}><DashboardPage /></Protected>} />
        <Route path="plantafel" element={<Protected requirePerm={['plantafel','view']}><PlantafelPage /></Protected>} />
        <Route path="mitarbeiter" element={<Protected requirePerm={['employees','view']}><EmployeesPage /></Protected>} />
        <Route path="projekte" element={<Protected requirePerm={['projects','view']}><ProjectsPage /></Protected>} />
        <Route path="projekte/:projectId" element={<Protected requirePerm={['projects','view']}><ProjectDetailPage /></Protected>} />
        <Route path="abwesenheiten" element={<Protected requirePerm={['absences','view']}><AbsencesPage /></Protected>} />
        <Route path="anwesenheit" element={<Protected requirePerm={['attendance_timesheets','view']}><AttendancePage /></Protected>} />
        <Route path="tagesansicht" element={<Protected requirePerm={['day_view','view']}><DayViewPage /></Protected>} />
        <Route path="einstellungen/*" element={<Protected requireAdmin><SettingsPage /></Protected>} />
        <Route path="aktivitaet" element={<Protected requireAdmin><ActivityLogPage /></Protected>} />
        <Route path="zeitimport" element={<Protected requirePerm={['time_import','view']}><TimeImportPage /></Protected>} />
        <Route path="geraete" element={<Protected requirePerm={['devices','view']}><DevicesPage /></Protected>} />
        <Route path="geraete-plantafel" element={<Protected requirePerm={['device_plantafel','view']}><DevicePlantafelPage /></Protected>} />
        <Route path="profil" element={<ProfilePage />} />
        <Route path="*" element={<StartSeite />} />
      </Route>
    </Routes>
  );
}

function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/*" element={<ProtectedShell />} />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <DataProvider>
          <ConfirmProvider>
            <AppRouter />
            <Toaster richColors position="top-right" />
          </ConfirmProvider>
        </DataProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
